import {ActionTree} from 'vuex'
import axios, {AxiosResponse, AxiosError} from "axios";
import Filter from '../ts/FilterInterface';

export const actions: ActionTree<any, any> = {

    /**
     * Search panelist with selected data variables
     */
    searchPanelists: ({state, getters, commit, dispatch}) => {

        commit('setPanelistsLoading', true);

        return new Promise((resolve, reject) => {

            axios.post('/panelists/search', state.search)

                .then(({data}) => {

                    commit('setSearchActive', true);
                    commit('setPanelists', data.panelists);
                    commit('setMeta', data.meta);
                    commit('setPanelistsLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setPanelistsLoading', false);
                    reject(error);
                });
        });
    },

    /**
     * Filter panelist with selected data variables
     */
    filterPanelists: ({state, getters, commit, dispatch}) => {

        commit('setPanelistsLoading', true);

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                throw new Error('No active filters could be found!');
            }

            const params = {
                query: filter.custom.jsonQuery,
                filter: filter.custom.jsonFilter,
                size: 100,
                ids: state.selectedVariableIds,
            };

            axios.post('/panelists/filter', params)

                .then((response) => {

                    commit('setPanelists', response.data.panelists);
                    commit('setMeta', response.data.meta);
                    commit('setPanelistsLoading', false);
                    resolve(response);
                })
                .catch(error => {
                    commit('setPanelistsLoading', false);
                    reject(error);
                })
        });
    },

    /**
     * Load panelist profile
     */
    loadProfile: ({state, getters, commit, dispatch}, id: string) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            axios.get('/panelist/' + id + '/profile')

                .then(({data}) => {
                    commit('setProfile', data.panelist);
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })

        });
    },

    /**
     * Set data variable for single profile (only supplied are updated)
     */
    setDataVariables: ({state, getters, commit, dispatch}, {panelist_id, data}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            axios.post('/panelist/' + panelist_id + '/data-variables', {data_variables: data})

                .then(({data}) => {
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })

        });
    },

    /**
     * Delete single profile
     */
    deleteProfile: ({state, getters, commit, dispatch}, panelist_id: number) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            axios.delete('/panelist/' + panelist_id)

                .then(({data}) => {
                    commit('deleteProfileFromSearchResults', panelist_id);
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })

        });
    },

    /**
     * Delete selected panelist IDs
     */
    deleteByIds: ({state, getters, commit, dispatch}, ids: Array<number>) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const ids = state.selectedRows.map((row: any) => row.id);

            if (ids.length === 0) {
                reject();
            }

            axios.post('/panelists/delete/by-ids', {ids: ids})

                .then(({data}) => {

                    ids.forEach((id: string) => {
                        commit('deleteProfileFromSearchResults', id);
                    });

                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })

        });
    },

    /**
     * Delete by Ids
     */
    deleteByFilter: ({state, getters, commit, dispatch}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                throw new Error('No active filters could be found!');
            }

            const params = {
                json_query: filter.custom.jsonQuery,
                json_filter: filter.custom.jsonFilter,
            };

            axios.post('/panelists/delete/by-filter', params)

                .then(({data}) => {
                    commit('clearSearchResults');
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })

        });
    },

    /**
     * Assign panelists to specific sub panels overriding prior memberships
     */
    changeSubPanelsByIds: ({state, getters, commit, dispatch}, subPanelId: number) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const ids = state.selectedRows.map((row: any) => row.id);

            if (ids.length === 0) {
                reject();
            }

            const data = {
                ids: ids,
                subPanelId: subPanelId
            };

            axios.post('/panelists/sub-panel/' + subPanelId + '/members/by-ids', data)

                .then(({data}) => {

                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })
        });
    },

    /**
     * Change sub panels by filter
     */
    changeSubPanelsByFilter: ({state, getters, commit, dispatch}, subPanelId: number) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                throw new Error('No active filters could be found!');
            }

            const data = {
                json_query: filter.custom.jsonQuery,
                json_filter: filter.custom.jsonFilter,
            };

            axios.post('/panelists/sub-panel/' + subPanelId + '/members/by-filter', data)

                .then(({data}) => {
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })
        });
    },

    /**
     * Change single data variable for specific set of panelist IDs
     */
    changeDataVariableByIds: ({state, getters, commit, dispatch}, {dataVariableId, value}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const ids = state.selectedRows.map((row: any) => row.id);

            if (ids.length === 0) {
                reject();
            }

            const data = {
                ids: ids,
                dataVariableId: dataVariableId,
                value: value
            };

            dispatch('changeDataVariableRequest', data)
                .then(response => {
                    resolve(null);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    /**
     * Send request out
     */
    changeDataVariableRequest: ({state, getters, commit, dispatch}, {ids, dataVariableId, value}) => {

        return new Promise((resolve, reject) => {

            const data = {
                ids: ids,
                value: value,
            }

            axios.post('/panelists/data-variable/' + dataVariableId + '/value/by-ids', data)

                .then(response => {

                    commit('updatePanelistsDataVariableByIds', data);
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })
        });
    },

    /**
     * Change data variable by filter
     */
    changeDataVariableByFilter: ({state, getters, commit, dispatch}, {dataVariableId, value}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                throw new Error('No active filters could be found!');
            }

            const params = {
                json_query: filter.custom.jsonQuery,
                json_filter: filter.custom.jsonFilter,
                value: value
            };

            axios.post('/panelists/data-variable/' + dataVariableId + '/value/by-filter', params)

                .then(response => {
                    commit('updateAllPanelistsDataVariable', {dataVariableId: dataVariableId, value: value});
                    commit('setProfileLoading', false);
                    resolve(null);
                })
                .catch(error => {
                    commit('setProfileLoading', false);
                    reject(error);
                })
        });
    },

    resetPassword: ({state, getters, commit, dispatch}, uuid) => {

        return new Promise((resolve, reject) => {

            axios.post('/panelist/' + uuid + '/reset-password')

                .then(response => {

                    resolve(null);

                })
                .catch(error => {

                    reject(error);
                })
        });
    },

    createPassword: ({state, getters, commit, dispatch}, uuid) => {

        return new Promise((resolve, reject) => {

            axios.post('/panelist/' + uuid + '/create-password')

                .then(response => {

                    resolve(null);

                })
                .catch(error => {

                    reject(error);
                })
        });
    },

    exportPanelistsByFilter: ({state, getters, commit, dispatch}, {decode}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                console.error('No active filters could be found!');
                reject();
            }

            const data = {
                decode: decode,
                json_query: filter.custom.jsonQuery,
                json_filter: filter.custom.jsonFilter,
                variable_ids: state.selectedVariableIds,
            };

            axios.post('/panelists/export', data)
                .then((response) => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    exportPanelistsByIds: ({state, getters, commit, dispatch}, {decode}) => {

        commit('setProfileLoading', true);

        return new Promise((resolve, reject) => {

            const panelistIds = state.selectedRows.map((row: any) => row.id);

            if (panelistIds.length === 0) {
                console.error('No panelist selected!');
                reject();
            }

            const data = {
                panelist_ids: panelistIds,
                decode: decode,
                variable_ids: state.selectedVariableIds,
            };

            axios.post('/panelists/export', data)
                .then((response) => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    sendCreatePasswordEmails: ({state, getters, commit, dispatch}) => {

        return new Promise((resolve, reject) => {

            const filter = state.filters.find((filter: Filter) => filter.model);

            if (!filter) {
                console.error('No active filters could be found!');
                reject();
            }

            const data = {
                json_query: filter.custom.jsonQuery,
                json_filter: filter.custom.jsonFilter,
            };

            axios.post('/panelists/send-create-password-email', data)
                .then((response) => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    }
}




